
import { Movie } from "@/types/movie";
import { ViewContext } from "@/components/movie/buttons/types";
import { useActionButtonState } from "./action-buttons/useActionButtonState";
import { useActionButtonContext } from "./action-buttons/useActionButtonContext";
import { useFavoriteHandler } from "./action-buttons/useFavoriteHandler";
import { usePlanToWatchHandler } from "./action-buttons/usePlanToWatchHandler";
import { useButtonPropsGenerator } from "./action-buttons/useButtonPropsGenerator";

interface UseUnifiedActionButtonsProps {
  movie: Movie;
  context: ViewContext;
  currentTab?: string;
  onToggleFavorite?: (e: React.MouseEvent) => void | Promise<void>;
  onTogglePlanToWatch?: (e: React.MouseEvent) => void | Promise<void>;
}

export const useUnifiedActionButtons = ({
  movie,
  context,
  currentTab,
  onToggleFavorite,
  onTogglePlanToWatch
}: UseUnifiedActionButtonsProps) => {
  
  // Get button context and handlers - uses universal system
  const {
    tmdbId,
    isValidMovie,
    favoriteVisualState,
    planToWatchVisualState,
    isImmediate,
    toggleFavorite,
    togglePlanToWatch,
    commitToggleFavorite,
    commitTogglePlanToWatch
  } = useActionButtonContext(movie, context, currentTab);

  // Individual button states
  const favoriteState = useActionButtonState();
  const watchlistState = useActionButtonState();

  // FIXED: Button click handlers using unified system without override logic
  const { handleFavoriteClick } = useFavoriteHandler({
    movie,
    tmdbId,
    isValidMovie,
    favoriteVisualState,
    planToWatchVisualState,
    isImmediate,
    context,
    currentTab,
    onToggleFavorite,
    commitToggleFavorite,
    toggleFavorite,
    setFavoriteDeselected: favoriteState.setIsDeselected
  });

  const { handlePlanToWatchClick } = usePlanToWatchHandler({
    movie,
    tmdbId,
    isValidMovie,
    favoriteVisualState,
    planToWatchVisualState,
    isImmediate,
    context,
    currentTab,
    onTogglePlanToWatch,
    commitTogglePlanToWatch,
    togglePlanToWatch,
    setPlanToWatchDeselected: watchlistState.setIsDeselected
  });

  // Generate button props using universal system
  const { getFavoriteButtonProps, getWatchlistButtonProps } = useButtonPropsGenerator({
    favoriteVisualState,
    planToWatchVisualState,
    favoriteState,
    watchlistState,
    handleFavoriteClick,
    handlePlanToWatchClick
  });

  return {
    getFavoriteButtonProps,
    getWatchlistButtonProps,
    // Legacy compatibility
    favoriteHovered: favoriteState.isHovered,
    watchlistHovered: watchlistState.isHovered,
    setFavoriteHovered: favoriteState.setIsHovered,
    setWatchlistHovered: watchlistState.setIsHovered
  };
};
